<template>
  <template v-if="isVisible">
    <Modal
      :show="true"
      :title="t('Comparação de Laudos')"
      size="large"
      data-testid="so-laudos-comparison-modal"
      :closeLabel="t('Fechar')"
      closeTestId="so-laudos-comparison-close-button"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <div v-if="isLoading" class="loading-container">
        <ProgressSpinner style="width: 3rem; height: 3rem" strokeWidth="6" />
        <p>{{ t('Carregando laudos...') }}</p>
      </div>

      <div v-else-if="errorMessage" class="error-container">
        <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: var(--p-red-500);"></i>
        <p>{{ errorMessage }}</p>
      </div>

      <div v-else class="comparison-grid">
        <div class="comparison-column">
          <h4 class="column-title">{{ t('Laudo de Entrada') }}</h4>
          <SOReportAttributesList
            :attributes="entryAttributes"
            :editable="false"
            :showEditToggle="false"
            testIdPrefix="comparison-entry"
          />
        </div>

        <div class="comparison-column">
          <h4 class="column-title">{{ t('Laudo de Saída') }}</h4>
          <SOReportAttributesList
            :attributes="exitAttributes"
            :editable="false"
            :showEditToggle="false"
            testIdPrefix="comparison-exit"
          />
        </div>
      </div>
    </Modal>
  </template>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import { useSOReportAttributeValues } from '../../composables/useSOReportAttributeValues';
import SOReportAttributesList from './SOReportAttributesList.vue';

const { t } = useI18n();
const { fetchLaudoAttributes } = useSOReportAttributeValues();

const isVisible = ref(false);
const isLoading = ref(false);
const errorMessage = ref<string | null>(null);
const entryAttributes = ref<any[]>([]);
const exitAttributes = ref<any[]>([]);

const handleClose = (): void => {
  log('SOLaudosComparisonModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  entryAttributes.value = [];
  exitAttributes.value = [];
  errorMessage.value = null;
};

interface OpenEventData {
  orderItemId: number;
}

const handleOpen = async (data?: OpenEventData): Promise<void> => {
  log('SOLaudosComparisonModal', 'handleOpen', 'Opening modal', data);
  if (!data?.orderItemId) {
    logError('SOLaudosComparisonModal', 'handleOpen', 'No orderItemId provided');
    return;
  }

  isVisible.value = true;
  isLoading.value = true;
  errorMessage.value = null;
  entryAttributes.value = [];
  exitAttributes.value = [];

  try {
    const [entryResult, exitResult] = await Promise.all([
      fetchLaudoAttributes(data.orderItemId, 'entry'),
      fetchLaudoAttributes(data.orderItemId, 'exit')
    ]);

    const mergeWithValues = (definitions: any[], values: any[]): any[] =>
      definitions.map((def: any) => {
        const val = values?.find((v: any) => v.so_rav_attribute_id === def.so_ra_id);
        return { ...def, value: val?.so_rav_value ?? null };
      });

    entryAttributes.value = mergeWithValues(entryResult?.definitions || [], entryResult?.values || []);
    exitAttributes.value = mergeWithValues(exitResult?.definitions || [], exitResult?.values || []);

    log('SOLaudosComparisonModal', 'handleOpen', 'Loaded attributes', {
      entryCount: entryAttributes.value.length,
      exitCount: exitAttributes.value.length
    });
  } catch (err: any) {
    logError('SOLaudosComparisonModal', 'handleOpen', 'Error loading laudos', err);
    errorMessage.value = err.message || t('Erro ao carregar laudos');
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  log('SOLaudosComparisonModal', 'onMounted', 'Modal mounted');
  eventBus.on('modal.laudos-comparison.open', handleOpen);
});

onBeforeUnmount(() => {
  log('SOLaudosComparisonModal', 'onBeforeUnmount', 'Cleaning up');
  eventBus.off('modal.laudos-comparison.open', handleOpen);
});
</script>

<style scoped>
.comparison-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  min-height: 300px;
}

.comparison-column {
  min-width: 0;
}

.column-title {
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--p-primary-color);
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 16px;
}

.error-container p {
  color: var(--p-red-500);
  font-weight: 500;
}

@media (max-width: 768px) {
  .comparison-grid {
    grid-template-columns: 1fr;
  }
}
</style>
